import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';

const API = API_URL.replace(/\/$/, '');

type AdminDashboardProps = {
  onNavigate?: (view: string) => void;
};

export default function AdminDashboard({ onNavigate }: AdminDashboardProps) {
  const [stats, setStats] = useState<any>(null);
  const [flags, setFlags] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string| null>(null);
  const [recentUsersOpen, setRecentUsersOpen] = useState(true);
  const [recentFlagsOpen, setRecentFlagsOpen] = useState(true);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  async function loadAll() {
    try {
      setLoading(true); setErr(null);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [s, f] = await Promise.all([
        axios.get(`${API_URL}/admin/stats`, { headers }),
        axios.get(`${API_URL}/admin/flags`, { headers }),
      ]);
      setStats(s.data || {});
      setFlags(f.data || []);
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, []);

  async function restrictUser(userId: string, minutes = 60) {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/admin/users/${userId}/restrict`, { minutes }, { headers });
      await loadAll();
    } catch {}
  }
  async function unrestrictUser(userId: string) {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/admin/users/${userId}/unrestrict`, {}, { headers });
      await loadAll();
    } catch {}
  }

  return (
    <div className="p-4 space-y-6 themed-page">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-heading">Admin Overview</h2>
        <div className="flex items-center gap-2">
          <button className="btn text-xs" onClick={loadAll}>Refresh</button>
        </div>
      </div>
      {err && <div className="text-red-500 text-sm">{err}</div>}

      {loading ? (
        <div className="text-theme-secondary">Loading…</div>
      ) : (
        <>
          {/* Quick KPIs */}
          <section className="p-4 rounded-xl themed-card">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-lg themed-card p-4 hover:shadow-lg transition-shadow">
                <div className="text-xs opacity-70">Total Users</div>
                <div className="text-2xl font-bold">{stats?.total}</div>
              </div>
              <div className="rounded-lg themed-card p-4 hover:shadow-lg transition-shadow">
                <div className="text-xs opacity-70">Real Users</div>
                <div className="text-2xl font-bold">{stats?.normal}</div>
              </div>
              <div className="rounded-lg themed-card p-4 hover:shadow-lg transition-shadow">
                <div className="text-xs opacity-70">Seeded Users</div>
                <div className="text-2xl font-bold">{stats?.seeded}</div>
              </div>
            </div>
          </section>

          {/* Quick actions */}
          <section className="p-4 rounded-xl themed-card">
            <h3 className="text-sm font-semibold text-heading mb-2">Quick Actions</h3>
            <div className="flex flex-wrap gap-2">
              <button className="btn text-xs" onClick={() => onNavigate?.('admin-stats')}>Open Full Stats</button>
              <button className="btn text-xs" onClick={() => onNavigate?.('admin-users')}>Manage Users</button>
              <button className="btn text-xs" onClick={() => onNavigate?.('admin-posts')}>Manage Posts</button>
              <button className="btn text-xs" onClick={() => onNavigate?.('admin-flags')}>Review Flags</button>
              <button className="btn text-xs" onClick={() => onNavigate?.('admin-verifications')}>Verification Requests</button>
            </div>
          </section>

          {/* Recent users */}
          <section className="p-4 rounded-xl themed-card">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-heading">Recent users</h3>
              <button className="btn text-xs" onClick={() => setRecentUsersOpen((v) => !v)}>{recentUsersOpen ? 'Hide' : 'Show'}</button>
            </div>
            {recentUsersOpen && (
              <ul className="text-xs mt-2 space-y-2 max-h-56 overflow-y-auto pr-1">
                {(stats?.recent || []).slice(0, 8).map((r: any) => (
                  <li key={r.id} className="rounded-md p-2 themed-card hover:opacity-90 transition">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{r.id}</span>
                      {r.seeded && (
                        <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] border" style={{ borderColor: 'var(--border)' }}>seeded</span>
                      )}
                    </div>
                    <div className="opacity-70">{new Date(r.joinedAt).toLocaleString()} • {r.role}</div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Recent flags */}
          <section className="p-4 rounded-xl themed-card">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-heading">Recent flags</h3>
              <button className="btn text-xs" onClick={() => setRecentFlagsOpen((v) => !v)}>{recentFlagsOpen ? 'Hide' : 'Show'}</button>
            </div>
            {recentFlagsOpen && (
              <ul className="text-xs mt-2 space-y-2 max-h-56 overflow-y-auto pr-1">
                {(flags || []).slice(0, 8).map((f: any) => (
                  <li key={f.id} className="rounded-lg themed-card p-2 hover:shadow-sm transition-shadow">
                    <div className="flex items-center justify-between">
                      <span className="px-2 py-0.5 rounded-full text-[11px] bg-amber-500/10 text-amber-400">{f.type}</span>
                      <span className="text-theme-secondary">{new Date(f.createdAt).toLocaleString()}</span>
                    </div>
                    <div className="mt-1">{f.contentSnippet}</div>
                    <div className="text-[11px] text-theme-secondary mt-1">User: {f.user?.username || ''} • Source: {f.sourceId}</div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
